'use client'

import { useEffect, useMemo, useState } from 'react'
import { isImageAttachment } from '@/contracts/mediaAttachments'
import { selectMainAttachmentsState } from '@/msgdeskRuntime/state'
import { useSubsStore } from '@/state/MsgTreeProvider'

function imageLabel(title?: string, filename?: string) {
  return String(title || filename || 'Music image').trim() || 'Music image'
}

export function MusicImageGallery({ showEmpty = false }: { showEmpty?: boolean }) {
  const { state } = useSubsStore()
  const attachments = useMemo(() => selectMainAttachmentsState(state), [state])
  const images = useMemo(
    () => attachments.filter(item => item.mediaKind === 'image' || isImageAttachment(item.filename || item.src)),
    [attachments],
  )
  const [selectedId, setSelectedId] = useState('')

  useEffect(() => {
    if (!images.length) { setSelectedId(''); return }
    if (!images.some(image => image.id === selectedId)) setSelectedId(images[0].id)
  }, [images, selectedId])

  if (!images.length) {
    return showEmpty
      ? <section className="mb-7 rounded-2xl border border-[#e1d5c8] bg-white p-5 text-sm text-[#85776c]">No images yet.</section>
      : null
  }

  const selected = images.find(image => image.id === selectedId) || images[0]
  return (
    <section className="mb-7 overflow-hidden rounded-3xl border border-[#e1d5c8] bg-white shadow-sm" aria-label="Music images" data-md-music-gallery>
      <header className="flex items-end justify-between gap-3 border-b border-[#eee5dc] px-5 py-4">
        <div><p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#9a7658]">Artwork</p><h2 className="text-xl font-semibold text-[#2f241d]">Images</h2></div>
        <span className="text-xs text-[#85776c]">{images.length}</span>
      </header>
      <div className="grid gap-4 p-4 md:grid-cols-[minmax(0,1fr)_220px] sm:p-5">
        <a className="flex min-h-72 items-center justify-center overflow-hidden rounded-2xl bg-[#f7f4ee]" href={selected.src} aria-label={`Open ${imageLabel(selected.title, selected.filename)}`}>
          <img className="max-h-[70vh] max-w-full object-contain" src={selected.src} width={selected.width} height={selected.height} alt={imageLabel(selected.title, selected.filename)} />
        </a>
        <div className="min-w-0">
          <h3 className="break-words font-semibold text-[#2f241d]">{imageLabel(selected.title, selected.filename)}</h3>
          {selected.description ? <p className="mt-2 text-sm text-[#6d6259]">{selected.description}</p> : null}
          <div className="mt-4 grid grid-cols-3 gap-2 md:grid-cols-2">
            {images.map((image, index) => (
              <button key={`${image.id}-${index}`} className={`overflow-hidden rounded-xl border p-1 ${image.id === selected.id ? 'border-[#6b3f24]' : 'border-[#e1d5c8]'}`} type="button" onClick={() => setSelectedId(image.id)} aria-label={`Show ${imageLabel(image.title, image.filename)}`}>
                <img className="aspect-square h-full w-full object-cover" src={image.thumbSrc || image.src} alt={imageLabel(image.title, image.filename)} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
